import { useState } from 'react'
import { 
  GoogleAuthProvider,
  signInWithPopup,
} from 'firebase/auth'
import { requireAuth } from '../../lib/firebase'
import { useUser } from '../../context/UserContext'
import { Shield, Lock, X } from 'lucide-react'

interface VerificationModalProps {
  isOpen: boolean
  onClose: () => void
  onVerified: () => void
}

const VerificationModal = ({ isOpen, onClose, onVerified }: VerificationModalProps) => {
  const { setUser } = useUser()
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const handleGoogleSignIn = async () => {
    setIsLoading(true)
    setError(null)

    try {
      const provider = new GoogleAuthProvider()
      // Add additional scopes if needed
      provider.addScope('profile')
      provider.addScope('email')
      
      const result = await signInWithPopup(requireAuth(), provider)
      
      if (result?.user) {
        const firebaseIdToken = await result.user.getIdToken()
        localStorage.setItem('firebase_auth_idToken', firebaseIdToken)
        
        setUser(result.user)
        onVerified()
        onClose()
      }
    } catch (err: any) {
      console.error('Error signing in with Google:', err)
      
      // Don't show error for popup-closed-by-user as it might be a false positive
      // or the user might have completed the sign-in in a different way
      if (err?.code === 'auth/popup-closed-by-user') {
        // Check if user is actually signed in despite the error
        const currentUser = requireAuth().currentUser
        if (currentUser) {
          // User is signed in, proceed
          const firebaseIdToken = await currentUser.getIdToken()
          localStorage.setItem('firebase_auth_idToken', firebaseIdToken)
          setUser(currentUser)
          onVerified()
          onClose()
          return
        }
        // If not signed in, silently fail (user might have cancelled intentionally)
        setIsLoading(false)
        return
      } else if (err?.code === 'auth/popup-blocked') {
        setError('Popup was blocked by your browser. Please allow popups for this site and try again.')
      } else if (err?.code === 'auth/cancelled-popup-request') {
        // Another popup is already open
        setError('Please wait for the current sign-in window to complete.')
      } else if (err?.code === 'auth/account-exists-with-different-credential') {
        setError('An account already exists with this email. Please use a different sign-in method.')
      } else {
        setError(err?.message || 'Failed to sign in with Google. Please try again.')
      }
      setIsLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md relative">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors z-10"
        >
          <X className="h-6 w-6" />
        </button>

        {/* Header */}
        <div className="bg-gradient-to-r from-primary-500 to-primary-600 px-6 py-8 text-center rounded-t-2xl">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">
            Verify Your Details
          </h2>
          <p className="text-primary-100 text-sm sm:text-base">
            Sign in with Google to continue booking
          </p>
        </div>

        {/* Content */}
        <div className="px-6 py-8 space-y-6">
          {/* Features */}
          <div className="grid grid-cols-2 gap-3">
            <div className="text-center p-3 rounded-xl bg-primary-50 border border-primary-100">
              <Shield className="w-6 h-6 mx-auto mb-1.5 text-primary-500" />
              <p className="text-xs font-medium text-gray-700">Secure</p>
            </div>
            <div className="text-center p-3 rounded-xl bg-primary-50 border border-primary-100">
              <Lock className="w-6 h-6 mx-auto mb-1.5 text-primary-500" />
              <p className="text-xs font-medium text-gray-700">Private</p>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3">
              <p className="text-sm text-red-700 text-center">{error}</p>
            </div>
          )}

          {/* Google Sign In */}
          <button
            type="button"
            onClick={handleGoogleSignIn}
            disabled={isLoading}
            className="w-full border-2 border-gray-200 hover:border-gray-300 bg-white text-gray-700 py-4 rounded-xl font-semibold text-base transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 shadow-md hover:shadow-lg"
          >
            {isLoading ? (
              <>
                <div className="w-5 h-5 border-3 border-gray-400/30 border-t-gray-400 rounded-full animate-spin"></div>
                <span>Verifying...</span>
              </>
            ) : (
              <>
                <svg className="w-6 h-6" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                <span>Continue with Google</span>
              </>
            )}
          </button>

          <p className="text-center text-xs text-gray-500 leading-relaxed pt-2">
            By continuing, you agree to our{' '}
            <a href="/terms" className="text-primary-500 hover:text-primary-600 underline">
              Terms
            </a>{' '}
            and{' '}
            <a href="/privacy" className="text-primary-500 hover:text-primary-600 underline">
              Privacy Policy
            </a>
          </p>
        </div>

        {/* Loading Overlay */}
        {isLoading && (
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center rounded-2xl">
            <div className="bg-white p-8 rounded-2xl shadow-2xl border border-gray-200">
              <div className="flex flex-col items-center gap-4">
                <div className="relative w-16 h-16">
                  <div className="absolute inset-0 border-4 border-primary-500/20 rounded-full"></div>
                  <div className="absolute inset-0 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
                <p className="text-base font-semibold text-gray-700">
                  Verifying with Google...
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default VerificationModal
